import { Clock } from "lucide-react";
import { formatContractDeadlineDetails } from "./calendarUtils";

type CalendarEventResource = {
    statusLabel?: string | null;
    clientName?: string | null;
    siteLabel?: string | null;
    siteContext?: string | null;
    durationLabel?: string | null;
    realStatus?: { label?: string | null } | null;
    isContractual?: boolean;
    realTimeLabel?: string | null;
    vehicleLabel?: string | null;
    realStartTime?: string | null;
    realEndTime?: string | null;
    contractId?: unknown;
    contractCode?: unknown;
    contractualEndDate?: unknown;
};

type CalendarEvent = {
    id?: string | number;
    title?: string | null;
    start: Date;
    end: Date;
    allDay?: boolean;
    resource?: CalendarEventResource | null;
};

export function CalendarEventCard({ event }: { event: CalendarEvent }) {
    const resource = event.resource;
    const statusLabel = resource?.statusLabel || resource?.realStatus?.label;
    const isContractual = resource?.isContractual;
    const durationLabel = resource?.durationLabel?.trim();
    const eventName = durationLabel ? resource?.siteLabel || resource?.clientName || "Instalación" : event.title;
    const realTimeLabel = resource?.realTimeLabel?.trim();
    const vehicleLabel = resource?.vehicleLabel?.trim();

    return (
        <div
            title={formatContractDeadlineDetails(resource) || undefined}
            className={`calendar-event-card ${durationLabel && resource?.siteLabel ? "calendar-event-card--labor" : ""} w-full overflow-hidden text-xs px-1 py-0.5 leading-tight ${realTimeLabel || vehicleLabel
                ? "calendar-event-card--with-real-time flex flex-col items-start gap-0.5"
                : "flex items-center justify-between gap-1"
                }`}
        >
            <div className="calendar-event-heading flex items-center justify-between gap-1 w-full min-w-0">
                <span data-testid="calendar-event-primary-site" className={`calendar-event-title ${durationLabel && resource?.siteLabel ? "calendar-event-title--wrap" : "truncate"} font-medium min-w-0`}>
                    {isContractual && <span className="mr-1 font-bold text-red-300">[C]</span>}
                    {eventName}
                </span>
                {durationLabel && (
                    <span data-testid="calendar-event-secondary-duration" className="calendar-event-duration shrink-0">{" · "}{durationLabel}</span>
                )}
                {statusLabel && (
                    <span className="calendar-event-status hidden sm:inline-block text-[10px] px-1 rounded bg-black/30 text-white shrink-0">
                        {statusLabel}
                    </span>
                )}
            </div>
            {durationLabel && (resource?.siteContext || resource?.clientName) && (
                <span className="calendar-event-site-context w-full truncate text-[10px] opacity-90" title={resource.siteContext || `Cliente: ${resource.clientName}`}>
                    {resource.siteContext || `Cliente: ${resource.clientName}`}
                </span>
            )}
            {realTimeLabel && (
                <span
                    role="note"
                    aria-label={realTimeLabel}
                    title={realTimeLabel}
                    data-testid="calendar-real-time-markers"
                    className="calendar-real-time-markers flex min-w-0 w-full items-start gap-1 font-medium text-[10px] leading-snug whitespace-normal break-words"
                >
                    <Clock aria-hidden="true" className="hidden sm:block h-3 w-3 shrink-0 mt-px" />
                    <span className="calendar-real-time-desktop hidden sm:inline break-words">{realTimeLabel}</span>
                    <span className="calendar-real-time-mobile sm:hidden flex min-w-0 flex-col" aria-hidden="true">
                        {resource?.realStartTime && (
                            <span className="calendar-real-time-mobile-pair">
                                <span className="calendar-real-time-mobile-label">Inicio</span>
                                <time
                                    className="calendar-real-time-mobile-time"
                                    dateTime={resource.realStartTime}
                                >
                                    {resource.realStartTime}
                                </time>
                            </span>
                        )}
                        {resource?.realEndTime && (
                            <span className="calendar-real-time-mobile-pair">
                                <span className="calendar-real-time-mobile-label">Fin</span>
                                <time
                                    className="calendar-real-time-mobile-time"
                                    dateTime={resource.realEndTime}
                                >
                                    {resource.realEndTime}
                                </time>
                            </span>
                        )}
                        {!resource?.realStartTime && !resource?.realEndTime && (
                            <span>{realTimeLabel}</span>
                        )}
                    </span>
                </span>
            )}
            {vehicleLabel && (
                <span
                    role="note"
                    aria-label={vehicleLabel}
                    title={vehicleLabel}
                    data-testid="calendar-vehicle-label"
                    className="calendar-vehicle-label flex min-w-0 w-full items-start font-medium text-[10px] leading-snug whitespace-normal break-words"
                >
                    {vehicleLabel}
                </span>
            )}
        </div>
    );
}
